import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import ApiErrorMessages from "../components/UI/ApiErrorMessages";
import Button from "../components/UI/Button";
import Checkbox from "../components/UI/Checkbox";
import ErrorMessages from "../components/UI/ErrorMessages";
import Input from "../components/UI/Input";
import Loader from "../components/UI/Loader";
import Modal from "../components/UI/Modal";
import Select from "../components/UI/Select";
import {
    BulkActionBatch,
    BulkActionBatchItem,
    BulkManagedParticipant,
    BulkParticipantActions,
    BulkParticipantFilters,
    BulkParticipantPreviewResponse,
    cancelBulkParticipantActionBatch,
    createBulkParticipantActionBatch,
    forceCancelBulkParticipantActionBatch,
    getBulkParticipantActionBatch,
    getBulkParticipantActionBatchItems,
    getBulkParticipantActionBatches,
    previewBulkParticipantActions
} from "../services/clientsService";
import { searchPrograms } from "../services/programsService";
import Program from "../models/serviceModels/Program";
import useElementVisible from "../hooks/useElementVisible";
import useSortableData from "../hooks/useSortableData";
import DataTable from "../components/UI/DataTable";
import { DataTableColumn } from "../models/DataTable";
import { sortableBulkParticipantManagementEnabled } from "../utils/features";
import "./BulkParticipantManagement.css";

const TAKE = 100;
const BATCH_ITEM_TAKE = 100;
const ACTIVE_BATCH_STATUSES = ["pending", "in_progress", "cancel_requested"];
const STATUS_DISPLAY_NAMES: Record<string, string> = {
    pending: "Pending",
    in_progress: "In Progress",
    cancel_requested: "Cancel Requested",
    cancelled: "Cancelled",
    completed: "Completed",
    completed_with_errors: "Completed with Errors",
    failed: "Failed",
    succeeded: "Succeeded"
};

const today = () => new Date().toISOString().slice(0, 10);
const DEACTIVATION_UNENROLL_REASON = "Participant is being deactivated";

const formatStatus = (status?: string | null) => {
    if (!status) {
        return "";
    }

    return STATUS_DISPLAY_NAMES[status] || status
        .split("_")
        .map(part => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ");
};

const BulkParticipantManagement = () => {
    const [programs, setPrograms] = useState<Program[]>([]);
    const [filters, setFilters] = useState<BulkParticipantFilters>({ inactiveDays: "90" });
    const [preview, setPreview] = useState<BulkParticipantPreviewResponse>();
    const [selectedClientIds, setSelectedClientIds] = useState<string[]>([]);
    const [selectAllMatching, setSelectAllMatching] = useState(false);
    const [deactivateSelected, setDeactivateSelected] = useState(false);
    const [deactivationDate, setDeactivationDate] = useState(today());
    const [deactivationUnenrollPrograms, setDeactivationUnenrollPrograms] = useState(true);
    const [deactivationUnenrollDate, setDeactivationUnenrollDate] = useState(today());
    const [deactivationUnenrollReason, setDeactivationUnenrollReason] = useState(DEACTIVATION_UNENROLL_REASON);
    const [unenrollProgramIds, setUnenrollProgramIds] = useState<string[]>([]);
    const [unenrollDate, setUnenrollDate] = useState(today());
    const [unenrollReason, setUnenrollReason] = useState("");
    const [loading, setLoading] = useState(true);
    const [previewing, setPreviewing] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [apiError, setApiError] = useState<any>();
    const [modalError, setModalError] = useState<string>();
    const [confirmPreview, setConfirmPreview] = useState<BulkParticipantPreviewResponse>();
    const [forceCancelBatchId, setForceCancelBatchId] = useState<string>();
    const [batches, setBatches] = useState<BulkActionBatch[]>([]);
    const [activeBatch, setActiveBatch] = useState<BulkActionBatch>();
    const [batchItems, setBatchItems] = useState<BulkActionBatchItem[]>([]);
    const [showRunningBatches, setShowRunningBatches] = useState(false);
    const [batchDetailsVisible, setBatchDetailsVisible] = useState(false);
    const [previewSkip, setPreviewSkip] = useState(0);
    const [previewPaging, setPreviewPaging] = useState(false);
    const [batchItemSkip, setBatchItemSkip] = useState(0);
    const [batchItemsPaging, setBatchItemsPaging] = useState(false);
    const [previewServerSort, setPreviewServerSort] = useState<{ sortBy?: string; sortDirection?: 'asc' | 'desc' }>({});
    const [participantPagingReference, participantPagingVisible] = useElementVisible<HTMLDivElement>();
    const [batchItemPagingReference, batchItemPagingVisible] = useElementVisible<HTMLDivElement>();
    const loaded = useRef(false);

    const selectedIdSet = useMemo(() => new Set(selectedClientIds), [selectedClientIds]);
    const runningBatches = useMemo(() => batches.filter(b => ACTIVE_BATCH_STATUSES.includes(b.status)), [batches]);
    const hasRunningBatches = runningBatches.length > 0;
    const hasMorePreviewParticipants = !!preview && preview.participants.data.length < preview.participants.totalCount;
    const hasMoreBatchItems = !!activeBatch && batchItems.length < activeBatch.totalCount;
    const getBatchSortValue = useCallback((batch: BulkActionBatch, key: string) => {
        if (key === 'progress') return batch.successCount + batch.failedCount + batch.cancelledCount;
        return batch[key as keyof BulkActionBatch];
    }, []);
    const getParticipantSortValue = useCallback((participant: BulkManagedParticipant, key: string) => {
        if (key === 'enrolledPrograms') return participant.enrolledPrograms.map(program => program.name).join(', ');
        return participant[key as keyof BulkManagedParticipant];
    }, []);
    const getBatchItemSortValue = useCallback((item: BulkActionBatchItem, key: string) => item[key as keyof BulkActionBatchItem], []);
    const { data: sortedRunningBatches, sortBy: runningSortBy, sortDirection: runningSortDirection, onSort: onRunningSort } = useSortableData(runningBatches, getBatchSortValue);
    const { data: sortedPreviewParticipants, sortBy: participantSortBy, sortDirection: participantSortDirection, onSort: onParticipantSort } = useSortableData(preview?.participants.data || [], getParticipantSortValue);
    const { data: sortedBatchItems, sortBy: batchItemSortBy, sortDirection: batchItemSortDirection, onSort: onBatchItemSort } = useSortableData(batchItems, getBatchItemSortValue);

    const selectedCount = selectAllMatching
        ? preview?.participants.totalCount || 0
        : selectedClientIds.length;
    const hasActionSelected = deactivateSelected || unenrollProgramIds.length > 0;
    const showSelectParticipantsMessage = hasActionSelected && selectedCount === 0;

    const actions: BulkParticipantActions = {
        deactivate: {
            enabled: deactivateSelected,
            date: deactivationDate,
            unenrollPrograms: deactivateSelected && deactivationUnenrollPrograms,
            unenrollProgramsDate: deactivateSelected && deactivationUnenrollPrograms ? deactivationUnenrollDate : undefined,
            unenrollProgramsReason: deactivateSelected && deactivationUnenrollPrograms ? deactivationUnenrollReason : undefined
        },
        unenroll: {
            programIds: unenrollProgramIds,
            stopDate: unenrollDate,
            stopReason: unenrollReason || null
        }
    };

    const selection = selectAllMatching
        ? { mode: "all" as const }
        : { mode: "ids" as const, clientIds: selectedClientIds };

    useEffect(() => {
        document.title = "Inactive Participants";
    }, []);

    useEffect(() => {
        Promise.all([
            searchPrograms({ query: "", skip: 0, take: 1000, includeDeactivated: false }),
            getBulkParticipantActionBatches()
        ]).then(([programResult, batchResult]) => {
            setPrograms(programResult.data);
            setBatches(batchResult);
            const firstActiveBatch = batchResult.find(b => ACTIVE_BATCH_STATUSES.includes(b.status));
            if (firstActiveBatch) {
                setActiveBatch(firstActiveBatch);
            }
            return loadPreviewPage(filters, actions, selection, 0, false);
        }).catch(e => {
            console.error(e);
            setApiError(e);
        }).finally(() => {
            setLoading(false);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!hasRunningBatches) {
            return;
        }

        const interval = window.setInterval(() => {
            if (activeBatch && ACTIVE_BATCH_STATUSES.includes(activeBatch.status)) {
                refreshBatch(activeBatch.id);
            } else {
                refreshBatches();
            }
        }, 5000);

        return () => window.clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeBatch?.id, activeBatch?.status, hasRunningBatches]);

    useEffect(() => {
        if (!loaded.current || previewSkip === 0) {
            return;
        }

        loadPreviewPage(filters, actions, selection, previewSkip, true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [previewSkip]);

    useEffect(() => {
        if (participantPagingVisible && hasMorePreviewParticipants && !previewing && !previewPaging) {
            setPreviewSkip(current => current + TAKE);
        }
    }, [participantPagingVisible, hasMorePreviewParticipants, previewing, previewPaging]);

    useEffect(() => {
        if (!loaded.current || batchItemSkip === 0 || !activeBatch) {
            return;
        }

        loadBatchItems(activeBatch.id, batchItemSkip, true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [batchItemSkip]);

    useEffect(() => {
        if (batchItemPagingVisible && batchDetailsVisible && hasMoreBatchItems && !batchItemsPaging) {
            setBatchItemSkip(current => current + BATCH_ITEM_TAKE);
        }
    }, [batchItemPagingVisible, batchDetailsVisible, hasMoreBatchItems, batchItemsPaging]);

    useEffect(() => {
        loaded.current = true;
    }, []);

    const loadPreviewPage = async (
        nextFilters: BulkParticipantFilters,
        nextActions: BulkParticipantActions,
        nextSelection: { mode: "all" } | { mode: "ids", clientIds: string[] },
        skip: number,
        append: boolean,
        sort = previewServerSort
    ) => {
        if (append) {
            setPreviewPaging(true);
        } else {
            setPreviewing(true);
        }
        setApiError(undefined);
        try {
            const result = await previewBulkParticipantActions({
                filters: nextFilters,
                actions: nextActions,
                selection: nextSelection,
                skip,
                take: TAKE,
                ...sort
            });
            setPreview(current => {
                if (!append || !current) {
                    return result;
                }

                return {
                    ...result,
                    participants: {
                        ...result.participants,
                        data: [
                            ...current.participants.data,
                            ...result.participants.data
                        ]
                    }
                };
            });
            return result;
        } catch (e) {
            console.error(e);
            setApiError(e);
        } finally {
            setPreviewing(false);
            setPreviewPaging(false);
        }
    };

    const getConfirmationPreview = async () => {
        setPreviewing(true);
        setApiError(undefined);
        try {
            return await previewBulkParticipantActions({
                filters,
                actions,
                selection,
                skip: 0,
                take: TAKE
            });
        } catch (e) {
            console.error(e);
            setApiError(e);
        } finally {
            setPreviewing(false);
        }
    };

    const loadBatchItems = async (batchId: string, skip: number, append: boolean) => {
        setBatchItemsPaging(true);
        try {
            const items = await getBulkParticipantActionBatchItems(batchId, skip, BATCH_ITEM_TAKE);
            setBatchItems(current => append ? [...current, ...items] : items);
            return items;
        } catch (e) {
            console.error(e);
            setApiError(e);
        } finally {
            setBatchItemsPaging(false);
        }
    };

    const refreshPreviewList = async () => {
        setPreviewSkip(0);
        setSelectedClientIds([]);
        setSelectAllMatching(false);
        await loadPreviewPage(filters, actions, { mode: "ids", clientIds: [] }, 0, false);
    };

    const refreshBatch = async (batchId: string, showDetails = false) => {
        try {
            const [batch, batchHistory] = await Promise.all([
                getBulkParticipantActionBatch(batchId),
                getBulkParticipantActionBatches()
            ]);
            setActiveBatch(batch);
            setBatches(batchHistory);
            if (showDetails) {
                setBatchItemSkip(0);
                const items = await loadBatchItems(batchId, 0, false);
                if (!items) {
                    setBatchItems([]);
                }
                setBatchDetailsVisible(true);
            }
            if (!ACTIVE_BATCH_STATUSES.includes(batch.status)) {
                await refreshPreviewList();
            }
        } catch (e) {
            console.error(e);
            setApiError(e);
        }
    };

    const refreshBatches = async () => {
        try {
            const batchHistory = await getBulkParticipantActionBatches();
            setBatches(batchHistory);
        } catch (e) {
            console.error(e);
            setApiError(e);
        }
    };

    const handleApplyFilters = (e: FormEvent) => {
        e.preventDefault();
        setSelectedClientIds([]);
        setSelectAllMatching(false);
        setPreviewSkip(0);
        setPreviewServerSort({});
        loadPreviewPage(filters, actions, { mode: "ids", clientIds: [] }, 0, false, {});
    };

    const handleParticipantSort = (key: string) => {
        onParticipantSort(key);
        if (!sortableBulkParticipantManagementEnabled) {
            return;
        }

        const sortDirection = participantSortBy === key && participantSortDirection === 'asc' ? 'desc' : 'asc';
        const sort = { sortBy: key, sortDirection } as const;
        setPreviewServerSort(sort);
        setPreviewSkip(0);
        loadPreviewPage(filters, actions, selection, 0, false, sort);
    };

    const handleDeactivationDateChange = (value: string) => {
        setDeactivationDate(current => {
            setDeactivationUnenrollDate(existing => existing === current ? value : existing);
            return value;
        });
    };

    const toggleClient = (client: BulkManagedParticipant, checked: boolean) => {
        if (selectAllMatching) {
            setSelectAllMatching(false);
        }

        if (checked) {
            setSelectedClientIds([...selectedClientIds, client.id]);
        } else {
            setSelectedClientIds(selectedClientIds.filter(id => id !== client.id));
        }
    };

    const toggleUnenrollProgram = (programId: string, checked: boolean) => {
        if (checked) {
            setUnenrollProgramIds([...unenrollProgramIds, programId]);
        } else {
            setUnenrollProgramIds(unenrollProgramIds.filter(id => id !== programId));
        }
    };

    const handleOpenConfirm = async () => {
        setModalError(undefined);
        setConfirmPreview(undefined);

        if (selectedCount === 0) {
            setModalError("Select at least one participant");
            return;
        }

        if (!hasActionSelected) {
            setModalError("Select at least one action");
            return;
        }

        const result = await getConfirmationPreview();
        if (result) {
            setConfirmPreview(result);
        }
    };

    const handleCreateBatch = async () => {
        setSubmitting(true);
        setApiError(undefined);
        try {
            const batch = await createBulkParticipantActionBatch({
                filters,
                actions,
                selection
            });
            setConfirmPreview(undefined);
            setSelectedClientIds([]);
            setSelectAllMatching(false);
            setActiveBatch(batch);
            await refreshBatch(batch.id);
        } catch (e) {
            console.error(e);
            setApiError(e);
        } finally {
            setSubmitting(false);
        }
    };

    const handleCancelBatch = async (batchId: string) => {
        setApiError(undefined);
        try {
            const batch = await cancelBulkParticipantActionBatch(batchId);
            setActiveBatch(batch);
            await refreshBatch(batchId);
        } catch (e) {
            console.error(e);
            setApiError(e);
        }
    };

    const handleForceCancelBatch = async () => {
        if (!forceCancelBatchId) {
            return;
        }

        setApiError(undefined);
        try {
            const batch = await forceCancelBulkParticipantActionBatch(forceCancelBatchId);
            setActiveBatch(batch);
            setForceCancelBatchId(undefined);
            await refreshBatch(forceCancelBatchId);
        } catch (e) {
            console.error(e);
            setApiError(e);
        }
    };

    const formatDate = (value?: string | null) => value ? value.slice(0, 10) : "No Activity";

    const formatDateTime = (value?: string | null) => {
        if (!value) {
            return "";
        }

        const date = new Date(value);
        if (!Number.isNaN(date.getTime())) {
            return date.toLocaleString(undefined, {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
                hour: "numeric",
                minute: "2-digit"
            });
        }

        return value.slice(0, 16).replace("T", " ");
    };

    const getProgramNames = (ids: string[]) => {
        return ids.map(id => programs.find(p => p.id === id)?.name || id).join(", ");
    };

    const getMainContent = () => {
        if (loading) {
            return <Loader />;
        }

        return (
            <section id="main-content" tabIndex={-1}>
                <div className="section">
                    <div className="bulk-management-shell">
                        <div className="bulk-page-header">
                            <h1>Inactive Participants</h1>
                            <Button id="view-bulk-action-history"
                                className="secondary"
                                text="Action History"
                                href="/bulk-management/history"
                                icon={<i className="ri-history-line"></i>}
                                iconLocation="start" />
                        </div>
                        <ApiErrorMessages error={apiError} />
                        {modalError && <ErrorMessages messages={[modalError]} />}

                        <form className="bulk-filters" onSubmit={handleApplyFilters}>
                        <div className="bulk-filter-field">
                            <Select id="bulk-program-filter"
                                label="Filter by Program"
                                value={filters.programId || ""}
                                onChange={(value) => setFilters({ ...filters, programId: value || null })}>
                                <option value="">All Programs</option>
                                {programs.map(program => <option key={program.id} value={program.id}>{program.name}</option>)}
                            </Select>
                        </div>
                        <div className="bulk-filter-field">
                            <Select id="inactive-days-filter"
                                label="Inactive Days"
                                value={filters.inactiveDays?.toString() || ""}
                                onChange={(value) => setFilters({ ...filters, inactiveDays: value || null })}>
                                <option value="">Any</option>
                                <option value="30">30 days</option>
                                <option value="60">60 days</option>
                                <option value="90">90 days</option>
                                <option value="180">180 days</option>
                                <option value="365">365 days</option>
                                <option value="no_activity">No Activity</option>
                            </Select>
                        </div>
                        <div className="bulk-filter-actions">
                            <Button id="apply-bulk-filters"
                                text="Apply"
                                type="submit"
                                loading={previewing}
                                disabled={previewing}
                                icon={<i className="ri-filter-3-line"></i>}
                                iconLocation="start" />
                        </div>
                    </form>

                    <div className="bulk-running-toggle-row">
                        <Button id="toggle-running-batches"
                            className="secondary"
                            text={`${showRunningBatches && hasRunningBatches ? "Hide" : "Show"} Running Actions (${runningBatches.length})`}
                            disabled={!hasRunningBatches}
                            onClick={() => setShowRunningBatches(!showRunningBatches)}
                            icon={<i className={showRunningBatches ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}></i>}
                            iconLocation="end" />
                    </div>

                    {showRunningBatches && hasRunningBatches && <div className="bulk-running-panel">
                        <div className="bulk-running-header">
                            <h2>Running Actions</h2>
                            <div className="bulk-running-header-actions">
                                <Button id="refresh-running-batches"
                                    className="secondary small"
                                    text="Refresh"
                                    onClick={refreshBatches}
                                    icon={<i className="ri-refresh-line"></i>}
                                    iconLocation="start" />
                            </div>
                        </div>
                        <div className="bulk-table-scroll">
                            <DataTable
                                columns={[
                                    { key: 'created', label: 'Created', sortable: true },
                                    { key: 'status', label: 'Status', sortable: true },
                                    { key: 'progress', label: 'Progress', sortable: true },
                                    { key: 'totalCount', label: 'Total', sortable: true },
                                    { key: 'successCount', label: 'Succeeded', sortable: true },
                                    { key: 'failedCount', label: 'Failed', sortable: true },
                                    { key: 'cancelledCount', label: 'Cancelled', sortable: true },
                                    { key: 'actions', label: '', sortable: false }
                                ] as DataTableColumn[]}
                                data={sortedRunningBatches}
                                sortBy={runningSortBy}
                                sortDirection={runningSortDirection}
                                onSort={onRunningSort}
                                sortingEnabled={sortableBulkParticipantManagementEnabled}
                                renderRow={batch => <tr key={batch.id} className="table-row">
                                        <td>{formatDateTime(batch.created)}</td>
                                        <td>{formatStatus(batch.status)}</td>
                                        <td>{batch.successCount + batch.failedCount + batch.cancelledCount} of {batch.totalCount}</td>
                                        <td>{batch.totalCount}</td>
                                        <td>{batch.successCount}</td>
                                        <td>{batch.failedCount}</td>
                                        <td>{batch.cancelledCount}</td>
                                        <td>
                                            <div className="bulk-table-actions">
                                                <Button id={`view-running-batch-${batch.id}`}
                                                    className="secondary small"
                                                    text="View"
                                                    onClick={() => refreshBatch(batch.id, true)} />
                                                {batch.status === "cancel_requested" ? <Button id={`force-cancel-running-batch-${batch.id}`}
                                                        className="warning small"
                                                        text="Force Cancel"
                                                        onClick={() => setForceCancelBatchId(batch.id)} />
                                                    : <Button id={`cancel-running-batch-${batch.id}`}
                                                        className="warning small"
                                                        text="Cancel"
                                                        onClick={() => handleCancelBatch(batch.id)} />}
                                            </div>
                                        </td>
                                    </tr>} />
                        </div>
                    </div>}

                    <div className="bulk-layout">
                        <div className="bulk-table-panel">
                            <div className="bulk-table-toolbar">
                                <Checkbox id="select-all-matching"
                                    checked={selectAllMatching}
                                    label={`Select All (${preview?.participants.totalCount || 0})`}
                                    containerClassName="unboxed"
                                    onChange={setSelectAllMatching}
                                    disabled={!preview || preview.participants.totalCount === 0} />
                                <div>{selectedCount} selected</div>
                            </div>
                            <div className="bulk-table-scroll">
                                <DataTable
                                    columns={[
                                        { key: 'selection', label: '', sortable: false },
                                        { key: 'name', label: 'Name', sortable: true },
                                        { key: 'phone', label: 'Phone', sortable: true },
                                        { key: 'lastActivity', label: 'Last Activity', sortable: true },
                                        { key: 'daysInactive', label: 'Days Inactive', sortable: true },
                                        { key: 'enrolledPrograms', label: 'Enrolled Programs', sortable: true }
                                    ] as DataTableColumn[]}
                                    data={sortedPreviewParticipants}
                                    sortBy={participantSortBy}
                                    sortDirection={participantSortDirection}
                                    onSort={handleParticipantSort}
                                    sortingEnabled={sortableBulkParticipantManagementEnabled}
                                    emptyMessage="No participants found"
                                    renderRow={client => (
                                            <tr key={client.id} className="table-row">
                                                <td>
                                                    <Checkbox id={`select-${client.id}`}
                                                        checked={selectAllMatching || selectedIdSet.has(client.id)}
                                                        containerClassName="unboxed"
                                                        onChange={(checked) => toggleClient(client, checked)}
                                                        disabled={selectAllMatching} />
                                                </td>
                                                <td>{client.name}</td>
                                                <td>{client.phone || ""}</td>
                                                <td>{formatDate(client.lastActivity)}</td>
                                                <td>{client.daysInactive === null || client.daysInactive === undefined ? "No Activity" : client.daysInactive}</td>
                                                <td>{client.enrolledPrograms.map(program => program.name).join(", ")}</td>
                                            </tr>
                                    )} />
                            </div>
                            {previewPaging && <Loader />}
                            {hasMorePreviewParticipants && <div ref={participantPagingReference} className="bulk-scroll-sentinel"></div>}
                        </div>

                        <div className="bulk-actions-panel">
                            <h2>Actions</h2>
                            <Checkbox id="bulk-deactivate"
                                checked={deactivateSelected}
                                onChange={setDeactivateSelected}
                                label="Deactivate participants" />
                            {deactivateSelected && <>
                                <Input id="bulk-deactivation-date"
                                    label="Deactivation Date"
                                    type="date"
                                    value={deactivationDate}
                                    onChange={handleDeactivationDateChange}
                                    required={true} />
                                <Checkbox id="bulk-deactivation-unenroll-programs"
                                    checked={deactivationUnenrollPrograms}
                                    onChange={setDeactivationUnenrollPrograms}
                                    label="Unenroll from all active programs" />
                                {deactivationUnenrollPrograms && <>
                                    <Input id="bulk-deactivation-unenroll-date"
                                        label="Program Exit Date"
                                        type="date"
                                        value={deactivationUnenrollDate}
                                        onChange={setDeactivationUnenrollDate} />
                                    <Input id="bulk-deactivation-unenroll-reason"
                                        label="Program Exit Reason"
                                        type="multi"
                                        value={deactivationUnenrollReason}
                                        onChange={setDeactivationUnenrollReason} />
                                </>}
                            </>}
                            <div className="bulk-action-group">
                                <div className="bulk-action-label">Remove from Program</div>
                                {programs.map(program => <Checkbox key={program.id}
                                    id={`unenroll-${program.id}`}
                                    checked={unenrollProgramIds.includes(program.id)}
                                    onChange={(checked) => toggleUnenrollProgram(program.id, checked)}
                                    label={program.name} />)}
                            </div>
                            {unenrollProgramIds.length > 0 && <>
                                <Input id="bulk-unenroll-date"
                                    label="Stop Date"
                                    type="date"
                                    value={unenrollDate}
                                    onChange={setUnenrollDate}
                                    required={true} />
                                <Input id="bulk-unenroll-reason"
                                    label="Stop Reason"
                                    type="multi"
                                    value={unenrollReason}
                                    onChange={setUnenrollReason} />
                            </>}
                            <Button id="open-bulk-confirm"
                                className="action full-width"
                                text="Create Action"
                                onClick={handleOpenConfirm}
                                disabled={previewing || submitting || selectedCount === 0}
                                icon={<i className="ri-play-list-add-line"></i>}
                                iconLocation="start" />
                            {showSelectParticipantsMessage && <div className="bulk-action-message">
                                Select at least one participant before creating this action.
                            </div>}
                        </div>
                    </div>

                </div>

                    <Modal id="bulk-confirm-modal"
                    visible={confirmPreview !== undefined}
                    className="small"
                    onClose={() => setConfirmPreview(undefined)}
                    title="Confirm Action">
                    {confirmPreview && <div>
                        <div className="bulk-confirm-counts">
                            <div><span>Total selected</span><strong>{confirmPreview.selection.totalSelected}</strong></div>
                            <div><span>Participants to deactivate</span><strong>{confirmPreview.selection.deactivateCount}</strong></div>
                        </div>
                        {(unenrollProgramIds.length > 0 || (deactivateSelected && deactivationUnenrollPrograms)) && <div className="bulk-confirm-programs">
                            {deactivateSelected && deactivationUnenrollPrograms ? <>
                                <div className="text-bold">Active programs to unenroll</div>
                                <div>All active program enrollments for deactivated participants</div>
                            </> : <>
                                <div className="text-bold">Programs selected</div>
                                <div>{getProgramNames(unenrollProgramIds)}</div>
                            </>}
                            <br />
                            <table className="table">
                                <thead>
                                    <tr>
                                        <td>Program</td>
                                        <td>Enrolled Participants</td>
                                    </tr>
                                </thead>
                                <tbody>
                                    {confirmPreview.selection.programCounts.map(program => <tr key={program.programId} className="table-row">
                                        <td>{program.programName}</td>
                                        <td>{program.count}</td>
                                    </tr>)}
                                </tbody>
                            </table>
                            <div className="bulk-warning">
                                {deactivateSelected && deactivationUnenrollPrograms
                                    ? "Only active program enrollments for participants being deactivated will be removed."
                                    : "Only participants enrolled in a selected program will be removed from that program."}
                            </div>
                        </div>}
                        <div className="row justify-content-between margin-top-5">
                            <div className="column-auto">
                                <Button id="cancel-bulk-create"
                                    text="Cancel"
                                    className="secondary"
                                    disabled={submitting}
                                    onClick={() => setConfirmPreview(undefined)} />
                            </div>
                            <div className="column-auto">
                                <Button id="confirm-bulk-create"
                                    text="Create Action"
                                    className="warning"
                                    loading={submitting}
                                    disabled={submitting}
                                    onClick={handleCreateBatch} />
                            </div>
                        </div>
                    </div>}
                </Modal>
                    <Modal id="bulk-batch-details-modal"
                    visible={batchDetailsVisible && activeBatch !== undefined}
                    className="medium"
                    onClose={() => setBatchDetailsVisible(false)}
                    title="Action Details">
                    {activeBatch && <div>
                        <div className="bulk-status-header">
                            <h2>Action Status</h2>
                            {ACTIVE_BATCH_STATUSES.includes(activeBatch.status) && <div className="bulk-table-actions">
                                {activeBatch.status === "cancel_requested" ? <Button id="force-cancel-active-batch"
                                        className="warning"
                                        text="Force Cancel"
                                        onClick={() => setForceCancelBatchId(activeBatch.id)}
                                        icon={<i className="ri-stop-circle-line"></i>}
                                        iconLocation="start" />
                                    : <Button id="cancel-active-batch"
                                        className="warning"
                                        text="Cancel"
                                        onClick={() => handleCancelBatch(activeBatch.id)}
                                        icon={<i className="ri-close-circle-line"></i>}
                                        iconLocation="start" />}
                            </div>}
                        </div>
                        <div className="bulk-metrics">
                            <div><span>Created</span><strong>{formatDateTime(activeBatch.created)}</strong></div>
                            <div><span>Status</span><strong>{formatStatus(activeBatch.status)}</strong></div>
                            <div><span>Total</span><strong>{activeBatch.totalCount}</strong></div>
                            <div><span>Succeeded</span><strong>{activeBatch.successCount}</strong></div>
                            <div><span>Failed</span><strong>{activeBatch.failedCount}</strong></div>
                            <div><span>Cancelled</span><strong>{activeBatch.cancelledCount}</strong></div>
                        </div>
                        {batchItems.length > 0 && <div className="bulk-table-scroll">
                            <DataTable
                                columns={[
                                    { key: 'clientName', label: 'Participant', sortable: true },
                                    { key: 'status', label: 'Status', sortable: true },
                                    { key: 'errorMessage', label: 'Error', sortable: true }
                                ] as DataTableColumn[]}
                                data={sortedBatchItems}
                                sortBy={batchItemSortBy}
                                sortDirection={batchItemSortDirection}
                                onSort={onBatchItemSort}
                                sortingEnabled={sortableBulkParticipantManagementEnabled}
                                renderRow={item => <tr key={item.id} className="table-row">
                                        <td>{item.clientName}</td>
                                        <td>{formatStatus(item.status)}</td>
                                        <td>{item.errorMessage || ""}</td>
                                    </tr>} />
                        </div>}
                        {batchItemsPaging && <Loader />}
                        {hasMoreBatchItems && <div ref={batchItemPagingReference} className="bulk-scroll-sentinel"></div>}
                    </div>}
                    </Modal>
                    <Modal id="force-cancel-bulk-action-modal"
                        visible={forceCancelBatchId !== undefined}
                        className="small"
                        onClose={() => setForceCancelBatchId(undefined)}
                        title="Force Cancel Action">
                        <div>
                            <p>
                                Force cancel immediately closes this action. Any participants still pending or in progress will be marked cancelled.
                            </p>
                            <p>
                                Participants already processed will not be rolled back.
                            </p>
                            <div className="row justify-content-between margin-top-5">
                                <div className="column-auto">
                                    <Button id="cancel-force-cancel-bulk-action"
                                        text="Cancel"
                                        className="secondary"
                                        disabled={submitting}
                                        onClick={() => setForceCancelBatchId(undefined)} />
                                </div>
                                <div className="column-auto">
                                    <Button id="confirm-force-cancel-bulk-action"
                                        text="Force Cancel"
                                        className="warning"
                                        disabled={submitting}
                                        onClick={handleForceCancelBatch} />
                                </div>
                            </div>
                        </div>
                    </Modal>
                </div>
            </section>
        );
    };

    return getMainContent();
};

export default BulkParticipantManagement;
